import { useEffect, useState } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { Truck, LogOut, ExternalLink } from "lucide-react";
import { useAuth, signInWithGoogle } from "@/context/AuthContext";
import { useLang } from "@/i18n/LanguageContext";
import { LegacyShell } from "@/components/PageShell";
import { formatCad } from "@/data/catalog";
import { AccountDogs } from "@/components/AccountDogs";
import { AccountStandingOrders } from "@/components/AccountStandingOrders";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (iso, lang) => (iso ? new Date(iso).toLocaleDateString(lang === "fr" ? "fr-CA" : "en-CA", { year: "numeric", month: "long", day: "numeric" }) : "");

const GoogleG = () => (
  <svg viewBox="0 0 48 48" className="w-4 h-4" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const SignIn = ({ t, failed }) => (
  <div className="max-w-md mx-auto text-center py-24" data-testid="account-signin">
    <p className="font-display italic text-[#B5A642]">{t("Your place in the ledger", "Votre place au registre")}</p>
    <h1 className="mt-3 font-display font-light text-4xl sm:text-5xl text-[#E8DDC7]">{t("Your Ledger", "Votre registre")}</h1>
    <p className="mt-5 text-[15px] leading-relaxed text-[#D3CEC4]/65">
      {t("Sign in with the Google account that matches your checkout email to see every order and follow your parcels.",
        "Connectez-vous avec le compte Google correspondant à votre courriel d'achat pour voir vos commandes et suivre vos colis.")}
    </p>
    {failed && <p data-testid="account-signin-failed" className="mt-4 text-[13px] text-[#C9876B]">{t("That sign-in didn't go through. Please try again.", "La connexion n'a pas abouti. Veuillez réessayer.")}</p>}
    <button data-testid="google-signin-button" onClick={signInWithGoogle}
      className="mt-8 inline-flex items-center gap-3 px-7 py-3.5 bg-[#E8DDC7] text-[#1C1F1E] text-[12px] uppercase tracking-[0.18em] rounded-sm hover:bg-white transition-colors duration-300">
      <GoogleG /> {t("Continue with Google", "Continuer avec Google")}
    </button>
    <p className="mt-6 text-[12px] text-[#D3CEC4]/40">{t("No account needed to shop. This is only for looking back.", "Aucun compte requis pour magasiner. C'est seulement pour consulter vos commandes.")}</p>
  </div>
);

const OrderCard = ({ o, t, lang }) => {
  const s = o.shipment;
  const status = o.refunded ? t("Refunded", "Remboursée") : o.shipped ? t("On its way", "En route") : t("Being prepared in the workshop", "En préparation à l'atelier");
  return (
    <li className="border border-[#303B2D]/70 rounded-sm p-6" data-testid={`account-order-${o.reference}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <p className="font-ledger text-[12px] text-[#B5A642]">Nº {o.reference}</p>
          <p className="mt-1 font-ledger text-[11px] text-[#D3CEC4]/45">{fmt(o.paid_at, lang)}{o.mode === "subscription" && ` · ${t("Standing Order", "Commande permanente")}`}</p>
        </div>
        <span data-testid={`account-order-status-${o.reference}`} className={`text-[11px] uppercase tracking-[0.2em] ${o.shipped ? "text-[#8FB08A]" : o.refunded ? "text-[#C9876B]" : "text-[#C9A96B]"}`}>{status}</span>
      </div>
      <ul className="mt-4 space-y-1.5">
        {(o.items || []).map((i) => (
          <li key={i.name} className="flex justify-between gap-4 text-[14px] text-[#D3CEC4]/80">
            <span>{i.qty} × {i.name}</span><span className="font-ledger">{formatCad(i.amount)}</span>
          </li>
        ))}
      </ul>
      <p className="mt-3 pt-3 border-t border-[#303B2D]/60 flex justify-between font-ledger text-[13px] text-[#E8DDC7]">
        <span>{t("Total", "Total")}</span><span>{formatCad(o.total)}</span>
      </p>
      {o.shipped && (
        <div className="mt-4 flex flex-wrap items-center gap-3 text-[13px] text-[#D3CEC4]/70" data-testid={`account-order-tracking-${o.reference}`}>
          <Truck className="w-4 h-4 text-[#B5A642]" strokeWidth={1.5} />
          <span>{t("Shipped", "Expédiée")} {fmt(o.shipped_at, lang)}{s?.carrier_label && ` · ${s.carrier_label}`}</span>
          {s?.tracking && (s.tracking_url ? (
            <a href={s.tracking_url} target="_blank" rel="noreferrer" data-testid={`account-track-${o.reference}`}
              className="inline-flex items-center gap-1.5 font-ledger text-[#B5A642] hover:text-[#E8DDC7] transition-colors">
              {s.tracking} <ExternalLink className="w-3 h-3" />
            </a>
          ) : <span className="font-ledger">{s.tracking}</span>)}
        </div>
      )}
    </li>
  );
};

export default function Account() {
  const { t, lang } = useLang();
  const { user, loading, logout } = useAuth();
  const location = useLocation();
  const [orders, setOrders] = useState(null);
  const failed = new URLSearchParams(location.search).get("signin") === "failed";

  useEffect(() => {
    document.title = t("Your Ledger — Dirty Paws Apothecary", "Votre registre — Dirty Paws Apothecary");
  }, [t]);
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/account/orders`, { withCredentials: true }).then((r) => setOrders(r.data)).catch(() => setOrders([]));
  }, [user]);

  return (
    <LegacyShell testid="account-page" noindex>
      <main className="max-w-3xl mx-auto px-5 md:px-10" data-no-translate>
        {loading && <p className="py-24 text-center text-[#D3CEC4]/50">…</p>}
        {!loading && !user && <SignIn t={t} failed={failed} />}
        {!loading && user && (
          <div className="py-20" data-testid="account-ledger">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                {user.picture && <img src={user.picture} alt="" className="w-12 h-12 rounded-full border border-[#B5A642]/40" referrerPolicy="no-referrer" />}
                <div>
                  <h1 className="font-display font-light text-4xl text-[#E8DDC7]" data-testid="account-name">{t("Welcome back", "Bon retour")}, {(user.name || "").split(" ")[0]}</h1>
                  <p className="text-[13px] text-[#D3CEC4]/50" data-testid="account-email">{user.email}</p>
                </div>
              </div>
              <button data-testid="account-logout" onClick={logout} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-[#D3CEC4]/50 hover:text-[#E8DDC7] transition-colors">
                <LogOut className="w-3.5 h-3.5" /> {t("Sign out", "Se déconnecter")}
              </button>
            </div>
            <div className="hairline mt-10" />
            <div className="mt-10"><AccountDogs t={t} lang={lang} /></div>
            <div className="hairline mt-12" />
            <AccountStandingOrders t={t} lang={lang} />
            <h2 className="mt-10 text-[10px] uppercase tracking-[0.3em] text-[#B5A642]/80">{t("Your orders", "Vos commandes")}</h2>
            {orders === null && <p className="mt-6 text-[#D3CEC4]/50">{t("Opening the ledger…", "Ouverture du registre…")}</p>}
            {orders && orders.length === 0 && (
              <div className="mt-6" data-testid="account-no-orders">
                <p className="font-display italic text-2xl text-[#D3CEC4]/60">{t("No orders under this address yet.", "Aucune commande à cette adresse pour l'instant.")}</p>
                <p className="mt-3 text-[13px] text-[#D3CEC4]/45">{t("Orders appear here when the checkout email matches your Google email.", "Les commandes apparaissent ici lorsque le courriel d'achat correspond à votre courriel Google.")}</p>
                <Link to="/shop" className="mt-6 inline-block text-[11px] uppercase tracking-[0.2em] text-[#B5A642]">{t("Shop the jars", "Voir les pots")}</Link>
              </div>
            )}
            {orders && orders.length > 0 && <ul className="mt-6 space-y-5">{orders.map((o) => <OrderCard key={o.reference} o={o} t={t} lang={lang} />)}</ul>}
          </div>
        )}
      </main>
    </LegacyShell>
  );
}
